/* -------------------------------------------------------------------------- */
/*                          Client global store                               */
/* -------------------------------------------------------------------------- */
'use client'
import { type QueryStatus, useQuery, type UseQueryOptions } from '@tanstack/react-query'
import { useEffect } from 'react'
import { create } from 'zustand'

import { allCoinsQueryOptions } from '@/services/coin/coins'
import type { GlobalCoin } from '@/types/coin'

import { devtoolsInNonProd } from './utils'

interface State {
  allCoins: GlobalCoin[]
  allCoinsStatus: QueryStatus | undefined
  allCoinsSlugMap: Record<string, GlobalCoin>
}

type GlobalStore = State

const initialState: State = {
  allCoins: [],
  allCoinsStatus: undefined,
  allCoinsSlugMap: {},
}

const useGlobalStore = create<GlobalStore>()(
  devtoolsInNonProd(
    () => ({
      ...initialState,
    }),
    {
      name: 'GlobalStore',
    },
  ),
)

export default useGlobalStore

const defaultGlobalStateQueryOptions: Omit<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  UseQueryOptions<any, any, any, any>,
  'queryKey' | 'queryFn'
> = {
  retry: 2,
  // Fresh for 5 minutes
  staleTime: 1000 * 60 * 5,
  refetchOnWindowFocus: false,
}

export function useInitGlobalStore() {
  const { data: allCoins, status: allCoinsStatus } = useQuery({
    ...defaultGlobalStateQueryOptions,
    ...allCoinsQueryOptions(),
  })
  useEffect(() => {
    if (Array.isArray(allCoins) && allCoins.length > 0) {
      const allCoinsSlugMap = allCoins.reduce<Record<string, GlobalCoin>>((acc, coin) => {
        acc[coin.slug] = coin
        return acc
      }, {})
      useGlobalStore.setState({ allCoins, allCoinsSlugMap })
    }
  }, [allCoins])
  useEffect(() => {
    useGlobalStore.setState({ allCoinsStatus })
  }, [allCoinsStatus])
}
